import { JournalType, LanguageType, SettingAreaPunchingType } from "@/enums/enums";
import { ContactModel } from "@/model/contact/contactModel";
import { ClassificationsItemModel, DocumentAccountModel, DocumentSummaryModel } from "@/model/document/documentModel";
import {
  DetailJournalDocumentModel,
  DocumentSubAccountModel,
  ElementJournalCertifiedModel,
  ElementJournalInfoModel,
  ElementJournalItemModel,
  FooterJournalDocumentModel,
  HeaderJournalDocumentModel,
  HeaderJournalItemColumnModel,
  JournalDocumentItemBaseModel,
  JournalDocumentModel,
  JournalDocumentTemplateModel,
  JournalOnlineViewModel,
  JournalReferenceModel,
  PurchaseTaxInvoiceModel,
} from "@/model/document/journalDocumentModal";
import {
  SettingJournalDocumentTemplateModel,
  SettingJournalTypeNameModel,
} from "@/model/document/settingDocumentTemplate";
import { MerchantDetail } from "@/model/merchant/merchantModel";
import { UserInfoModel } from "@/model/user/userModel";
import { GetJournalSettingDocument } from "@/service/document/dailyJournal/dailyJournalFacade";
import {
  GetClassificationsItemsModel,
  GetDocumentAccount,
  GetDocumentContact,
  GetDocumentMerchant,
  GetDocumentSummary,
  GetDocumentTags,
  GetDocumentUser,
  GetJournalReference,
} from "./documentFacadeHelper";
import {
  GetClassificationsList,
  GetContact,
  GetHeadType,
  GetMerchant,
  GetMerchantLogo,
  GetSignatureUrlUserApprove,
  GetSignatureUrlUserCreate,
  SetLanguage,
} from "./documentHelper";
import { GetJournalNameTypeModel } from "./shareHelper";

export function GetJournalDocumentTemplate(
  documentModel: JournalOnlineViewModel,
  settingDocumentTemplateDefault?: SettingJournalDocumentTemplateModel
): JournalDocumentTemplateModel {
  const documentTemplate = new JournalDocumentTemplateModel();

  documentTemplate.transactionType = documentModel.transactionType;
  documentTemplate.financeType = documentModel.financeType;
  documentTemplate.expenseType = documentModel.expenseType;
  documentTemplate.header = GetJournalHeaderDocument(documentModel, settingDocumentTemplateDefault);
  documentTemplate.detail = GetJournalBodyDocument(documentModel);
  documentTemplate.footer = GetJournalFooterDocment(documentModel, settingDocumentTemplateDefault);
  documentTemplate.isDeleted = documentModel.document.data.isDeleted;

  return documentTemplate;
}

export function GetJournalFooterDocment(
  documentModel: JournalOnlineViewModel,
  settingDocumentTemplateDefault?: SettingJournalDocumentTemplateModel
): FooterJournalDocumentModel {
  const footer: FooterJournalDocumentModel = {
    credit: documentModel.document.data.totalCredit,
    debit: documentModel.document.data.totalDabit,
    certified: GetJournalCertified(documentModel, settingDocumentTemplateDefault),
  };
  return footer;
}

export function GetJournalCertified(
  documentModel: JournalOnlineViewModel,
  settingDocumentTemplateDefault?: SettingJournalDocumentTemplateModel
): ElementJournalCertifiedModel {
  let certified: ElementJournalCertifiedModel = new ElementJournalCertifiedModel();
  certified = {
    userCreateTitle: {
      value: documentModel.template.certified.userCreateTitle.value,
      isShowElement: documentModel.template.certified.userCreateTitle.isShowElement,
    },
    userApproveTitle: {
      value: documentModel.template.certified.userApproveTitle.value,
      isShowElement: documentModel.template.certified.userApproveTitle.isShowElement,
    },
    userReviewedTitle: {
      value: documentModel.template.certified.userReviewedTitle.value,
      isShowElement: documentModel.template.certified.userReviewedTitle.isShowElement,
    },
    userApprove: {
      isShowSignatureUrl: documentModel.template.certified.isShowUserApproveUrl ?? true,
      isShowApproveDate: documentModel.template.certified.isShowUserApproveDate ?? true,
      isShowApproveName: documentModel.template.certified.isShowUserApproveName ?? true,
      approveDate: new Date(documentModel.document.data.approvedDateTime),
      name:
        documentModel.document.data.approvedUser.firstName + " " + documentModel.document.data.approvedUser.lastName,
      signatureUrl: GetSignatureUrlUserApprove(documentModel),
    },
    userCreate: {
      isShowSignatureUrl: documentModel.template.certified.isShowUserCreateUrl ?? true,
      isShowCreateDate: documentModel.template.certified.isShowUserCreateDate ?? true,
      isShowCreateName: documentModel.template.certified.isShowUserCreateName ?? true,
      createDate: new Date(documentModel.document.data.createdDateTime),
      name: documentModel.document.data.createdUser.firstName + " " + documentModel.document.data.createdUser.lastName,
      signatureUrl: GetSignatureUrlUserCreate(documentModel),
    },
    userReviewed: {
      isShowSignatureUrl: documentModel.template.certified.isShowUserReviewedUrl ?? true,
      isShowReviewedDate: documentModel.template.certified.isShowUserReviewedDate ?? true,
      reviewedDate: new Date(documentModel.document.data.reviewedDateTime),
      name:
        documentModel.document.data.reviewedUser.firstName + " " + documentModel.document.data.reviewedUser.lastName,
      signatureUrl: GetSignatureUrlUserReviewed(documentModel),
    },
    isCustomTextDisplay: documentModel.template.certified.isCustomTextDisplay,
    isShowQRCode: documentModel.template.certified.isShowQRCode ?? true,
  };

  if (settingDocumentTemplateDefault) {
    certified = {
      userCreateTitle: {
        value: settingDocumentTemplateDefault.certified.userCreateTitle.value,
        isShowElement: settingDocumentTemplateDefault.certified.userCreateTitle.isShowElement,
      },
      userApproveTitle: {
        value: settingDocumentTemplateDefault.certified.userApproveTitle.value,
        isShowElement: settingDocumentTemplateDefault.certified.userApproveTitle.isShowElement,
      },
      userReviewedTitle: {
        value: settingDocumentTemplateDefault.certified.userReviewedTitle.value,
        isShowElement: settingDocumentTemplateDefault.certified.userReviewedTitle.isShowElement,
      },
      userApprove: {
        isShowSignatureUrl: settingDocumentTemplateDefault.certified.isShowUserApproveUrl ?? true,
        isShowApproveDate: settingDocumentTemplateDefault.certified.isShowUserApproveDate ?? true,
        isShowApproveName: settingDocumentTemplateDefault.certified.isShowUserApproveName ?? true,
        approveDate: new Date(documentModel.document.data.approvedDateTime),
        name:
          documentModel.document.data.approvedUser.firstName + " " + documentModel.document.data.approvedUser.lastName,
        signatureUrl: GetSignatureUrlUserApprove(documentModel),
      },
      userCreate: {
        isShowSignatureUrl: settingDocumentTemplateDefault.certified.isShowUserCreateUrl ?? true,
        isShowCreateDate: settingDocumentTemplateDefault.certified.isShowUserCreateDate ?? true,
        isShowCreateName: settingDocumentTemplateDefault.certified.isShowUserCreateName ?? true,
        createDate: new Date(documentModel.document.data.createdDateTime),
        name:
          documentModel.document.data.createdUser.firstName + " " + documentModel.document.data.createdUser.lastName,
        signatureUrl: GetSignatureUrlUserCreate(documentModel),
      },
      userReviewed: {
        isShowSignatureUrl: settingDocumentTemplateDefault.certified.isShowUserReviewedUrl ?? true,
        isShowReviewedDate: settingDocumentTemplateDefault.certified.isShowUserReviewedDate ?? true,
        reviewedDate: new Date(documentModel.document.data.updatedDateTime),
        name:
          documentModel.document.data.reviewedUser.firstName + " " + documentModel.document.data.reviewedUser.lastName,
        signatureUrl: GetSignatureUrlUserReviewed(documentModel),
      },
      isCustomTextDisplay: settingDocumentTemplateDefault.certified.isCustomTextDisplay,
      isShowQRCode: settingDocumentTemplateDefault.certified.isShowQRCode ?? true,
    };
  }
  return certified;
}

export function GetSignatureUrlUserReviewed(documentModel: JournalOnlineViewModel) {
  let userReviewed = documentModel.credential.userCredential.find(
    (x) => x.userId === documentModel.document.data.reviewedUser.userId
  );
  if (userReviewed) {
    return userReviewed.signatureUrl;
  }
  return "";
}

export function GetJournalHeaderDocument(
  documentModel: JournalOnlineViewModel,
  settingDocumentTemplateDefault?: SettingJournalDocumentTemplateModel
): HeaderJournalDocumentModel {
  let defaultTemplate: SettingJournalDocumentTemplateModel = GetJournalSettingDocument(documentModel.transactionType);

  const headerDocument: HeaderJournalDocumentModel = {
    colorCode: documentModel.template.colorCode,
    colorCodeCopy: documentModel.template.colorCodeCopy ?? documentModel.template.colorCode,
    isShowColorBackground: documentModel.template.isShowColorBackground ?? defaultTemplate.isShowColorBackground,
    isShowColorBorder: documentModel.template.isShowColorBorder ?? defaultTemplate.isShowColorBorder,
    isShowColorBackgroundCopy:
      documentModel.template.isShowColorBackgroundCopy ?? defaultTemplate.isShowColorBackgroundCopy,
    isShowColorBorderCopy: documentModel.template.isShowColorBorderCopy ?? defaultTemplate.isShowColorBorderCopy,
    language: documentModel.template.language ?? LanguageType.Th,
    areaPunchingType: documentModel.template.areaPunchingType ?? SettingAreaPunchingType.None,
    merchantLogo: GetMerchantLogo(documentModel, settingDocumentTemplateDefault),
    headType: GetHeadType(documentModel, settingDocumentTemplateDefault),
    documentNameType: documentModel.template.documentNameType ?? GetJournalNameTypeDefault(),
    merchant: GetMerchant(documentModel, settingDocumentTemplateDefault),
    contact: GetContact(documentModel, settingDocumentTemplateDefault),
    documentInfo: GetJournalInfo(documentModel, settingDocumentTemplateDefault),
    description: documentModel.document.data.description,
    classifications: GetClassificationsList(documentModel),
  };

  if (settingDocumentTemplateDefault) {
    headerDocument.colorCode = settingDocumentTemplateDefault.colorCode;
    headerDocument.colorCodeCopy = settingDocumentTemplateDefault.colorCodeCopy;
    headerDocument.isShowColorBackground = settingDocumentTemplateDefault.isShowColorBackground;
    headerDocument.isShowColorBorder = settingDocumentTemplateDefault.isShowColorBorder;
    headerDocument.isShowColorBackgroundCopy = settingDocumentTemplateDefault.isShowColorBackgroundCopy;
    headerDocument.isShowColorBorderCopy = settingDocumentTemplateDefault.isShowColorBorderCopy;
    headerDocument.language = settingDocumentTemplateDefault.language;
    headerDocument.areaPunchingType = settingDocumentTemplateDefault.areaPunchingType;
    headerDocument.documentNameType = settingDocumentTemplateDefault.documentNameType;
  }
  SetLanguage(headerDocument.language);
  return headerDocument;
}

export function GetJournalNameTypeDefault(): SettingJournalTypeNameModel {
  let documentNameType: SettingJournalTypeNameModel = {
    purchaseJournal: GetJournalNameTypeModel(JournalType.PurchaseJournal),
    salesJournal: GetJournalNameTypeModel(JournalType.SalesJournal),
    paymentJournal: GetJournalNameTypeModel(JournalType.PaymentJournal),
    receiveJournal: GetJournalNameTypeModel(JournalType.ReceiveJournal),
    generalJournal: GetJournalNameTypeModel(JournalType.GeneralJournal),
  };
  return documentNameType;
}

export function GetJournalInfo(
  documentModel: JournalOnlineViewModel,
  settingDocumentTemplateDefault?: SettingJournalDocumentTemplateModel
): ElementJournalInfoModel {
  const journalInfo: ElementJournalInfoModel = {
    documentNoTitle: {
      value: documentModel.template.documentInfo.documentNoTitle.value,
      isShowElement: documentModel.template.documentInfo.documentNoTitle.isShowElement,
    },
    issedDateTitle: {
      value: documentModel.template.documentInfo.issedDateTitle.value,
      isShowElement: documentModel.template.documentInfo.issedDateTitle.isShowElement,
    },
    refTitle: {
      value: documentModel.template.documentInfo.refTitle.value,
      isShowElement: documentModel.template.documentInfo.refTitle.isShowElement,
    },
    jonalTypeTitle: {
      value: documentModel.template.documentInfo.jonalTypeTitle.value,
      isShowElement: documentModel.template.documentInfo.jonalTypeTitle.isShowElement,
    },
    journalRefTaxDateTitle: {
      value: documentModel.template.documentInfo.journalRefTaxDateTitle.value,
      isShowElement:
        documentModel.document.data.purchaseTaxInvoices.length > 0 &&
        documentModel.document.data.purchaseTaxInvoices[0].taxInvoiceDateTime
          ? true
          : false,
    },
    journalRefTaxNumberTitle: {
      value: documentModel.template.documentInfo.journalRefTaxNumberTitle.value,
      isShowElement:
        documentModel.document.data.purchaseTaxInvoices.length > 0 &&
        documentModel.document.data.purchaseTaxInvoices[0].taxInvoiceNumber
          ? true
          : false,
    },
    isCustomTextDisplay: documentModel.template.documentInfo.isCustomTextDisplay,
    dateOptionType: documentModel.template.documentInfo.dateOptionType,
    documentNo: documentModel.document.data.journalNumber,
    issedDate: new Date(documentModel.document.data.journalDate),
    ref: documentModel.document.data.reference ? documentModel.document.data.reference.referenceNumber : "-",
    jonalType: documentModel.document.data.journalType,
    journalRefTaxDate:
      documentModel.document.data.purchaseTaxInvoices.length > 0 &&
      documentModel.document.data.purchaseTaxInvoices[0].taxInvoiceDateTime
        ? new Date(documentModel.document.data.purchaseTaxInvoices[0].taxInvoiceDateTime)
        : new Date(),
    journalRefTaxNumber:
      documentModel.document.data.purchaseTaxInvoices.length > 0 &&
      documentModel.document.data.purchaseTaxInvoices[0].taxInvoiceNumber
        ? documentModel.document.data.purchaseTaxInvoices[0].taxInvoiceNumber
        : "",
    isShowClassification: documentModel.template.documentInfo.isShowClassification ?? false,
    infoClassificationList: documentModel.template.documentInfo.infoClassificationList ?? [],
  };

  if (settingDocumentTemplateDefault) {
    journalInfo.documentNoTitle = {
      value: settingDocumentTemplateDefault.documentInfo.documentNoTitle.value,
      isShowElement: settingDocumentTemplateDefault.documentInfo.documentNoTitle.isShowElement,
    };
    journalInfo.issedDateTitle = {
      value: settingDocumentTemplateDefault.documentInfo.issedDateTitle.value,
      isShowElement: settingDocumentTemplateDefault.documentInfo.issedDateTitle.isShowElement,
    };
    journalInfo.refTitle = {
      value: settingDocumentTemplateDefault.documentInfo.refTitle.value,
      isShowElement: settingDocumentTemplateDefault.documentInfo.refTitle.isShowElement,
    };
    journalInfo.jonalTypeTitle = {
      value: settingDocumentTemplateDefault.documentInfo.jonalTypeTitle.value,
      isShowElement: settingDocumentTemplateDefault.documentInfo.jonalTypeTitle.isShowElement,
    };
    journalInfo.journalRefTaxDateTitle.value = settingDocumentTemplateDefault.documentInfo.journalRefTaxDateTitle.value;
    journalInfo.journalRefTaxNumberTitle.value =
      settingDocumentTemplateDefault.documentInfo.journalRefTaxNumberTitle.value;
    journalInfo.isCustomTextDisplay = settingDocumentTemplateDefault.documentInfo.isCustomTextDisplay;
    journalInfo.dateOptionType = settingDocumentTemplateDefault.documentInfo.dateOptionType;
  }
  return journalInfo;
}

export function GetJournalBodyDocument(documentModel: JournalOnlineViewModel): DetailJournalDocumentModel {
  const detail: DetailJournalDocumentModel = {
    itemList: GetJournalItemList(documentModel),
  };
  return detail;
}

export function GetJournalItemList(documentModel: JournalOnlineViewModel): Array<ElementJournalItemModel> {
  const journalItemList = new Array<ElementJournalItemModel>();
  const itemstList = documentModel.document.data.items;

  if (itemstList && itemstList.length > 0) {
    itemstList.forEach((element) => {
      const productItem: ElementJournalItemModel = {
        description: element.description ? element.description : "",
        credit: element.credit ? element.credit : 0,
        debit: element.debit ? element.debit : 0,
        accountCode: element.account.accountCode,
        accountName: element.account.name,
        accountNameEn: element.account.nameEn,
        subAccountCode: element.subAccount.subAccountCode ? element.subAccount.subAccountCode : "-",
      };

      journalItemList.push(productItem);
    });
  }

  return journalItemList;
}

export function GetJournalDocumentData(data: any): JournalDocumentModel {
  const documentDataModel: JournalDocumentModel = {
    approvedDateTime: data.approvedDateTime ? data.approvedDateTime.ToDateTimezone() : "",
    approvedUser: data.approvedUser ? GetDocumentUser(data.approvedUser) : new UserInfoModel(),
    contact: data.contact ? GetDocumentContact(data.contact) : new ContactModel(),
    createdDateTime: data.createdDateTime ? data.createdDateTime.ToDateTimezone() : "",
    createdUser: data.createdUser ? GetDocumentUser(data.createdUser) : new UserInfoModel(),
    items: data.items ? GetJournalDocumentItemBase(data.items) : new Array<JournalDocumentItemBaseModel>(),
    merchant: data.merchant ? GetDocumentMerchant(data.merchant) : new MerchantDetail(),
    statusType: data.statusType ? data.statusType : 0,
    summary: data.summary ? GetDocumentSummary(data.summary) : new DocumentSummaryModel(),
    tags: data.tags ? GetDocumentTags(data.tags) : [],
    isDeleted: data.isDeleted ? data.isDeleted : false,
    updatedDateTime: data.updatedDateTime ? data.updatedDateTime.ToDateTimezone() : "",
    reviewedDateTime: data.reviewedDateTime ? data.reviewedDateTime.ToDateTimezone() : "",
    updatedUser: data.updatedUser ? GetDocumentUser(data.updatedUser) : new UserInfoModel(),
    reviewedUser: data.reviewedUser ? GetDocumentUser(data.reviewedUser) : new UserInfoModel(),
    description: data.description ? data.description : "",
    journalDate: data.journalDate ? data.journalDate : "",
    journalId: data.journalId ? data.journalId : "",
    journalNumber: data.journalNumber ? data.journalNumber : "",
    journalType: data.journalType ? data.journalType : JournalType.None,
    totalCredit: data.totalCredit ? data.totalCredit : 0,
    totalDabit: data.totalDabit ? data.totalDabit : 0,
    reference: data.reference ? GetJournalReference(data.reference) : new JournalReferenceModel(),
    purchaseTaxInvoices: data.purchaseTaxInvoices ? data.purchaseTaxInvoices : new Array<PurchaseTaxInvoiceModel>(),
    classifications: data.classifications
      ? GetClassificationsItemsModel(data.classifications)
      : new Array<ClassificationsItemModel>(),
  };

  return documentDataModel;
}

export function GetJournalDocumentItemBase(data: any): Array<JournalDocumentItemBaseModel> {
  const journalDocumentItemBaseModel = new Array<JournalDocumentItemBaseModel>();
  for (let documentItemBase of data) {
    journalDocumentItemBaseModel.push({
      description: documentItemBase.description ? documentItemBase.description : "",
      account: documentItemBase.account ? GetDocumentAccount(documentItemBase.account) : new DocumentAccountModel(),
      credit: documentItemBase.credit ? documentItemBase.credit : 0,
      debit: documentItemBase.debit ? documentItemBase.debit : 0,
      subAccount: documentItemBase.subAccount
        ? GetDocumentSubAccount(documentItemBase.subAccount)
        : new DocumentSubAccountModel(),
    });
  }
  return journalDocumentItemBaseModel;
}

export function GetDocumentSubAccount(data: any): DocumentSubAccountModel {
  const documentAccountModel: DocumentSubAccountModel = {
    subAccountCode: data.subAccountCode ? data.subAccountCode : "",
    subAccountId: data.subAccountId ? data.subAccountId : 0,
    subAccountRefType: data.subAccountRefType ? data.subAccountRefType : 0,
  };
  return documentAccountModel;
}

function GetInputForSetColumn(documentTemplate: JournalDocumentTemplateModel) {
  let subAccountNo = "";
  if (documentTemplate.detail.itemList && documentTemplate.detail.itemList.length > 0) {
    for (const productItem of documentTemplate.detail.itemList) {
      if (subAccountNo.length <= productItem.subAccountCode.length) {
        subAccountNo = productItem.subAccountCode;
      }
    }
  }

  return { subAccountNo };
}

export function GetHeaderJournalColumn(
  documentTemplate: JournalDocumentTemplateModel,
  isSetAreaPunching = false
): HeaderJournalItemColumnModel {
  let descriptionWidth = isSetAreaPunching ? 637 : 715; //maxWidth 753px
  const input = GetInputForSetColumn(documentTemplate);
  let headerItemColumn: HeaderJournalItemColumnModel = {
    accountNo: {
      isShowColumn: true,
      width: 60,
      align: "left",
    },
    subAccountNo: {
      isShowColumn: true,
      width: GetWidthColumnFromLengthNumberForSubAccount(input.subAccountNo),
      align: "left",
    },
    accountName: {
      isShowColumn: true,
      width: 120,
      align: "left",
    },
    description: {
      isShowColumn: true,
      width: 0,
      align: "left",
    },
    debit: {
      isShowColumn: true,
      width: 90,
      align: "right",
    },
    credit: {
      isShowColumn: true,
      width: 90,
      align: "right",
    },
  };

  for (const [key, value] of Object.entries(headerItemColumn)) {
    if (value.isShowColumn) {
      descriptionWidth -= value.width;
    }
  }

  headerItemColumn.description.width = descriptionWidth;

  return headerItemColumn;
}

function GetWidthColumnFromLengthNumberForSubAccount(numberStr: string) {
  let widthColumn = 60;
  numberStr = numberStr.replace(",", "");
  numberStr = numberStr.replace(".", "");

  if (numberStr.length > 10) {
    widthColumn = 120;
  } else if (numberStr.length > 7) {
    widthColumn = 80;
  }

  return widthColumn;
}
